import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bell, Check, CheckCheck, Trash2, Archive, X, ChevronRight,
  Clock, AlertTriangle, Info, CheckCircle, XCircle, Settings,
  RefreshCw, Filter, Package, DollarSign, Users, ShoppingCart,
  Monitor
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const NotificationCenter = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get(`/notifications?unread_only=${filter === 'unread'}&limit=50`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const handleMarkRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleArchive = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/archive`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification archived');
    } catch (err) {
      toast.error('Failed to archive');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleClearAll = async () => {
    try {
      await api.delete('/notifications/clear-read');
      setNotifications(prev => prev.filter(n => !n.read));
      toast.success('Read notifications cleared');
    } catch (err) {
      toast.error('Failed to clear notifications');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      handleMarkRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
      onClose();
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'stock': return <Package className="w-5 h-5 text-orange-500" />;
      case 'finance': return <DollarSign className="w-5 h-5 text-emerald-500" />;
      case 'hr': return <Users className="w-5 h-5 text-purple-500" />;
      case 'sales': return <ShoppingCart className="w-5 h-5 text-blue-500" />;
      case 'system': return <Monitor className="w-5 h-5 text-gray-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getNotificationIcon = (type, priority, category) => {
    if (priority === 'critical') return <AlertTriangle className="w-5 h-5 text-red-500" />;
    if (category) return getCategoryIcon(category);
    if (type.includes('approved') || type.includes('executed')) return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (type.includes('rejected')) return <XCircle className="w-5 h-5 text-red-500" />;
    if (type.includes('pending') || type.includes('created')) return <Clock className="w-5 h-5 text-yellow-500" />;
    return <Info className="w-5 h-5 text-blue-500" />;
  };

  const getPriorityBorder = (priority) => {
    switch (priority) {
      case 'critical': return 'border-l-4 border-l-red-500';
      case 'high': return 'border-l-4 border-l-amber-500';
      case 'medium': return 'border-l-4 border-l-blue-400';
      case 'low': return 'border-l-4 border-l-gray-300';
      default: return '';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div 
        className="absolute right-4 top-16 w-96 bg-white rounded-2xl shadow-2xl border overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#4A7C59]" />
              <h3 className="font-semibold">Notifications</h3>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="p-1 hover:bg-gray-200 rounded-lg text-[#4A7C59]"
                  title="Mark all as read"
                >
                  <CheckCheck className="w-4 h-4" />
                </button>
              )}
              {notifications.some(n => n.read) && (
                <button
                  onClick={handleClearAll}
                  className="p-1 hover:bg-red-100 rounded-lg text-gray-400 hover:text-red-500"
                  title="Clear read notifications"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Filter */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 text-sm rounded-lg ${
                filter === 'all' ? 'bg-[#4A7C59] text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 py-1 text-sm rounded-lg ${
                filter === 'unread' ? 'bg-[#4A7C59] text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              Unread
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.read ? 'bg-blue-50/50' : ''
                  } ${getPriorityBorder(notification.priority)}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type, notification.priority, notification.category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notification.read ? 'font-semibold' : ''}`}>
                          {notification.title}
                        </p>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {formatTime(notification.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      {notification.sender_name && (
                        <p className="text-xs text-gray-400 mt-1">
                          From: {notification.sender_name}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex flex-col gap-1">
                      {!notification.read && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMarkRead(notification.id); }}
                          className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
                          title="Mark as read"
                        >
                          <Check className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleArchive(notification.id); }}
                        className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
                        title="Archive"
                      >
                        <Archive className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  {(notification.action_url || NOTIFICATION_ACTION_MAP[notification.type]) && (
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!notification.read) handleMarkRead(notification.id);
                          const action = NOTIFICATION_ACTION_MAP[notification.type];
                          const url = notification.action_url || (action && action.url);
                          if (url) { navigate(url); onClose(); }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-[#4A7C59] text-white rounded-lg hover:bg-[#3d6649] transition-colors"
                      >
                        {NOTIFICATION_ACTION_MAP[notification.type]?.label || 'Take Action'}
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t bg-gray-50 text-center">
            <button
              onClick={() => { navigate('/notifications'); onClose(); }}
              className="text-sm text-[#4A7C59] hover:underline"
            >
              View all notifications
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const NOTIFICATION_ACTION_MAP = {
  low_stock: { label: 'View Inventory', url: '/inventory' },
  po_submitted: { label: 'Review PO', url: '/purchase-orders' },
  po_approved: { label: 'View PO', url: '/purchase-orders' },
  po_rejected: { label: 'View PO', url: '/purchase-orders' },
  leave_submitted: { label: 'Review Leave', url: '/hr/leave' },
  leave_approved: { label: 'View Leave', url: '/hr/leave' },
  leave_rejected: { label: 'View Leave', url: '/hr/leave' },
  expiry_alert: { label: 'View Inventory', url: '/inventory' },
  order_ready: { label: 'View Orders', url: '/sales' },
  order_completed: { label: 'View Orders', url: '/sales' },
  low_stock_alert: { label: 'View Inventory', url: '/inventory' },
  budget_overspend_alert: { label: 'View Finance', url: '/finance' },
  overdue_invoice_alert: { label: 'View Finance', url: '/finance' },
  pending_leave_alert: { label: 'Review Leave', url: '/hr/leave' },
};

export { NOTIFICATION_ACTION_MAP };

// Notification Bell Button Component
export const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastSeenCount, setLastSeenCount] = useState(0);
  const [flashBell, setFlashBell] = useState(false);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const res = await api.get('/notifications/unread-count');
        const newCount = res.data.unread_count || 0;
        
        if (newCount > lastSeenCount && lastSeenCount > 0) {
          try {
            const notifRes = await api.get('/notifications?unread_only=true&limit=5');
            const newNotifs = notifRes.data.notifications || [];
            const highPriority = newNotifs.filter(n => 
              n.priority === 'high' || n.priority === 'critical'
            );
            if (highPriority.length > 0) {
              const latest = highPriority[0];
              toast.warning(latest.title, {
                description: latest.message?.substring(0, 100),
                duration: 6000,
                action: latest.action_url ? {
                  label: 'View',
                  onClick: () => window.location.href = latest.action_url
                } : undefined,
              });
              setFlashBell(true);
              setTimeout(() => setFlashBell(false), 2000);
            }
          } catch (e) {}
        }
        
        setLastSeenCount(newCount);
        setUnreadCount(newCount);
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [lastSeenCount]);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`relative p-2 hover:bg-gray-100 rounded-lg transition-colors ${flashBell ? 'animate-bounce' : ''}`}
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${flashBell ? 'text-amber-500' : 'text-gray-600'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-red-500/30">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      <NotificationCenter isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default NotificationCenter;
